import type { ReactNode } from "react";
import { Dialog, Fade, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

export interface DialogShellProps {
  open: boolean;
  /** Dismiss intent (corner X, backdrop, Escape). Ignored entirely under `blocking`. */
  onClose: () => void;
  /** Disable every dismissal affordance without unmounting (e.g. while a confirm is in flight). */
  closeDisabled?: boolean;
  /** No Escape, no backdrop close, no corner X — for must-acknowledge dialogs. */
  blocking?: boolean;
  /** Defaults to `!blocking`. */
  showCloseButton?: boolean;
  maxWidth?: number;
  titleId: string;
  descriptionId?: string;
  children: ReactNode;
}

/**
 * Internal chrome shared by the kit dialogs: bordered elevation-0 paper,
 * blurred backdrop, 200ms fade, corner X, aria wiring. Paper radius comes
 * from the theme's MuiDialog styleOverride — do not set it here.
 */
export function DialogShell({
  open,
  onClose,
  closeDisabled = false,
  blocking = false,
  showCloseButton,
  maxWidth = 420,
  titleId,
  descriptionId,
  children,
}: DialogShellProps) {
  const dismissable = !blocking && !closeDisabled;

  return (
    <Dialog
      open={open}
      onClose={dismissable ? onClose : undefined}
      disableEscapeKeyDown={!dismissable}
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      TransitionComponent={Fade}
      transitionDuration={200}
      PaperProps={{
        elevation: 0,
        sx: {
          minWidth: 380,
          maxWidth,
          overflow: "visible",
          border: "1px solid",
          borderColor: "divider",
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            backdropFilter: "blur(4px)",
          },
        },
      }}
    >
      {(showCloseButton ?? !blocking) && (
        <IconButton
          onClick={onClose}
          disabled={closeDisabled}
          size="small"
          aria-label="Close"
          sx={{
            position: "absolute",
            right: 12,
            top: 12,
            color: "text.secondary",
            "&:hover": { backgroundColor: "action.hover" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
      {children}
    </Dialog>
  );
}
